<!-- Toolbar -->
<mat-toolbar *ngIf="isUserLoggedIn | async">
  <button class="header-nav" mat-icon-button aria-label="Toggle navigation menu" (click)="toggleMenu()">
    <mat-icon>menu</mat-icon>
  </button>
  <div class="header">
    <div class="flex-item"><img src="assets/img/main/logo-small.png?v=2" height="50" alt="Conexa Technologies"></div>
    <div *ngIf="userDetails" class="flex-item header-actions">
      <!-- Global search: one box across every workspace the user's role can access -->
      <app-global-search></app-global-search>
      <!-- Notification bell (payroll request activity etc.) -->
      <app-notification-bell></app-notification-bell>
      <!-- View as [Role] - ADMINS ONLY (gated on the real role, so it survives any active preview).
           Picking a role swaps every client-side gate to that role's grants; the amber chip + the
           banner below the toolbar show the preview is on. -->
      <button *ngIf="isRealAdmin()" type="button" class="role-view-chip" [class.viewing]="roleView"
              [matMenuTriggerFor]="roleViewMenu" (menuOpened)="loadViewableRoles()"
              aria-label="View the portal as another role">
        <mat-icon class="role-view-icon">visibility</mat-icon>
        <span class="role-view-label">{{ roleView ? 'Viewing: ' + roleView.roleName : 'View as' }}</span>
        <mat-icon class="profile-caret">arrow_drop_down</mat-icon>
      </button>
      <mat-menu #roleViewMenu="matMenu" xPosition="before" class="role-view-menu-panel">
        <button *ngIf="roleView" mat-menu-item (click)="endRoleView()">
          <mat-icon>visibility_off</mat-icon>
          <span>Exit - back to my Admin view</span>
        </button>
        <button *ngFor="let role of viewableRoles" mat-menu-item
                [disabled]="roleView?.roleId === role.id" (click)="startRoleView(role)">
          <mat-icon>{{ roleView?.roleId === role.id ? 'visibility' : 'badge' }}</mat-icon>
          <span>{{ role.description }}</span>
        </button>
        <div *ngIf="viewableRoles.length === 0" class="role-view-menu-empty">Loading roles...</div>
      </mat-menu>
      <button type="button" class="profile-chip" [matMenuTriggerFor]="profileMenu" aria-label="Account menu">
        <span class="profile-avatar">
          <img *ngIf="avatarUrl" [src]="avatarUrl" class="profile-photo" alt="" />
          <span *ngIf="!avatarUrl">{{ initials }}</span>
        </span>
        <span class="profile-copy">
          <span class="profile-name">{{ userDetails.name }}</span>
          <span class="profile-role" *ngIf="roleLabel">{{ roleLabel }}</span>
        </span>
        <mat-icon class="profile-caret">arrow_drop_down</mat-icon>
      </button>
      <mat-menu #profileMenu="matMenu" xPosition="before" class="profile-menu-panel">
        <button mat-menu-item routerLink="/settings">
          <mat-icon>settings</mat-icon>
          <span>Settings</span>
        </button>
        <button mat-menu-item (click)="logOut()">
          <mat-icon>logout</mat-icon>
          <span>Log Out</span>
        </button>
      </mat-menu>
    </div>
  </div>
</mat-toolbar>
<!-- Role-preview banner: unmissable while "View as [Role]" is active, on every page (explicit
     colors so all 10 themes read). The sidebar/tiles/guards below reflect the previewed role. -->
<div class="role-view-banner" *ngIf="(isUserLoggedIn | async) && roleView">
  <mat-icon>visibility</mat-icon>
  <span class="role-view-banner-text">
    Viewing as <strong>{{ roleView.roleName }}</strong> - pages and data are scoped to what this
    role can access. Anything you do still records as you.
  </span>
  <button type="button" class="role-view-exit" (click)="endRoleView()">Exit view</button>
</div>
<!-- No `autosize`: the drawer is a fixed 232px, and autosize's continuous re-measuring is what
     left partially-rendered drawer "dead pixels" behind when navigating between workspaces. -->
<mat-sidenav-container class="example-container">
    <!-- Side Nav -->
    <!-- Desktop starts expanded unless the user collapsed it last time (hamburger toggles it). -->
    <mat-sidenav *ngIf="isUserLoggedIn | async" #sidenav [mode]="isMobile ? 'over' : 'side'" [opened]="!isMobile && !isCollapsed" class="example-sidenav">
      <mat-nav-list class="sidenav-list">
        <!-- Grouped sections in the command-center sidebar style: uppercase section labels and
             two-line items (title + muted description). A section label only shows when the role
             can see at least one of its items; each item still gates on its own menu grant. -->

        <!-- Dashboard - pinned at the top; the dashboard itself serves as shortcuts to everything
             the user can access (incl. Resource Links, which was removed from this menu). -->
        <div class="nav-section-label">Overview</div>
        <a class="dashboard" mat-list-item routerLink="/home" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">dashboard</span>
          <span class="nav-copy">
            <span class="nav-title">Dashboard</span>
            <span class="nav-sub">Your dashboard</span>
          </span>
        </a>
        <!-- IS&T Support lives on the Dashboard as a tile (per Casey 2026-08-13), not in this
             sidebar - it is still visible to EVERYONE signed in via /support (no gate). -->

        <div class="nav-section-label" *ngIf="showOperationsModule() || showCSuiteModule() || hasSupervisorAreaAccess() || hasAttendanceAreaAccess() || hasPtoAreaAccess()">Performance</div>
        <a *ngIf="showOperationsModule()" mat-list-item routerLink="/operations" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">engineering</span>
          <span class="nav-copy">
            <span class="nav-title">Operations</span>
            <span class="nav-sub">Provider command center</span>
          </span>
        </a>
        <a *ngIf="showCSuiteModule()" mat-list-item routerLink="/csuite" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">insights</span>
          <span class="nav-copy">
            <span class="nav-title">C Suite</span>
            <span class="nav-sub">Executive financials</span>
          </span>
        </a>
        <!-- Supervisor is feature-gated (supervisor), not menu-gated - it lives in Performance per
             Casey 2026-07-15. Same icon as its dashboard tile (one feature, one icon). -->
        <a *ngIf="hasSupervisorAreaAccess()" mat-list-item routerLink="/supervisor" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">supervisor_account</span>
          <span class="nav-copy">
            <span class="nav-title">Supervisor</span>
            <span class="nav-sub">Team check-ins and uptime</span>
          </span>
        </a>
        <!-- Attendance is feature-gated (attendance), not menu-gated - it lives in Performance next to
             Supervisor. Every holder also sees their own attendance. -->
        <a *ngIf="hasAttendanceAreaAccess()" mat-list-item routerLink="/attendance" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">event_available</span>
          <span class="nav-copy">
            <span class="nav-title">Attendance
              <ng-container *ngIf="attendancePending$ | async as pending">
                <span class="attendance-pending-badge" *ngIf="pending.total > 0"
                      [attr.aria-label]="pending.total + ' pending attendance correction requests'"
                      [matTooltip]="pending.total + ' pending correction requests you can approve'">{{ pending.total }}</span>
              </ng-container>
            </span>
            <span class="nav-sub">Days worked and days off</span>
          </span>
        </a>
        <!-- PTO is feature-gated (pto-requests / pto-approve-manager), not menu-gated - employees
             request their own time off, managers approve, payroll finalizes. -->
        <a *ngIf="hasPtoAreaAccess()" mat-list-item routerLink="/pto" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">beach_access</span>
          <span class="nav-copy">
            <span class="nav-title">Time Off Requests</span>
            <span class="nav-sub">Request and approve time off</span>
          </span>
        </a>

        <div class="nav-section-label" *ngIf="showBillingModule() || showSalesModule() || showHrModule()">Execution</div>
        <a *ngIf="showBillingModule()" mat-list-item routerLink="/billing" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">receipt_long</span>
          <span class="nav-copy">
            <span class="nav-title">Billing</span>
            <span class="nav-sub">Timesheets, QA and exports</span>
          </span>
        </a>
        <a *ngIf="showSalesModule()" mat-list-item routerLink="/sales" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">trending_up</span>
          <span class="nav-copy">
            <span class="nav-title">Sales</span>
            <span class="nav-sub">Leads and tracking</span>
          </span>
        </a>
        <a *ngIf="showHrModule()" mat-list-item routerLink="/hr" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">diversity_3</span>
          <span class="nav-copy">
            <span class="nav-title">Human Resources</span>
            <span class="nav-sub">Payroll and compensation</span>
          </span>
        </a>

        <div class="nav-section-label" *ngIf="showIstModule() || showAdministratorModule()">Governance</div>
        <!-- IS&T: onboarding, company email / Entra accounts and password resets. Sits above
             Administrator - it provisions the accounts Administrator then governs. -->
        <a *ngIf="showIstModule()" mat-list-item routerLink="/ist" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">dns</span>
          <span class="nav-copy">
            <span class="nav-title">IS&amp;T</span>
            <span class="nav-sub">Onboarding, accounts and access</span>
          </span>
        </a>
        <a *ngIf="showAdministratorModule()" mat-list-item routerLink="/administrator" routerLinkActive="active-list-item">
          <span class="material-symbols-outlined icon">admin_panel_settings</span>
          <span class="nav-copy">
            <span class="nav-title">Administrator</span>
            <span class="nav-sub">Users, roles and access</span>
          </span>
        </a>

        <!-- Settings + Log Out both live in the top-bar profile menu (the AI assistant's launcher
             occupies the bottom-left corner). -->
      </mat-nav-list>
    </mat-sidenav>
  <!-- Main Content -->
    <mat-sidenav-content>
      <!-- Conexa Flow route transition (fade + 12px rise), keyed on the activated route via the
           standard outlet-data pattern. [@.disabled] gates it off for reduced-motion users. -->
      <div class="route-host" [@.disabled]="reducedMotion" [@routeFadeUp]="prepareRoute(outlet)">
        <router-outlet #outlet="outlet"></router-outlet>
      </div>
    </mat-sidenav-content>
</mat-sidenav-container>
<!-- AI billing assistant - bottom-left launcher + chat panel, on every page for granted roles -->
<app-assistant-chat *ngIf="(isUserLoggedIn | async) && has(FeatureKey.AiAssistant)"></app-assistant-chat>
<!-- Service announcement dialog (story 919 timekeeping notice) - pops on every sign-in until acknowledged -->
<app-service-announcement *ngIf="isUserLoggedIn | async"></app-service-announcement>
<!-- <app-footer></app-footer> -->
